import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getTasks, deleteTask as deleteTaskApi, getUpcomingTasks } from '../services/api';
import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import { notifyDataChanged } from '../utils/dataEvents';
import type { CreatorInfo } from '../utils/permissions';
import { entityStoreMessages, removeItemsWhere } from './storeHelpers';

export interface Task extends CreatorInfo {
  id: string;
  userId: string; // 建立者（權限判斷用）
  customerId: string;
  title: string;
  description?: string | null;
  dueDate: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  customer?: {
    id: string;
    name: string;
    email?: string;
    company?: string;
    archived?: boolean;
  };
}

// 使用 createEntityStore 建立基礎 store
const useTaskStoreBase = createEntityStore<Task>({
  entityName: 'task',
  fetchFn: getTasks,
  deleteFn: deleteTaskApi,
  toast,
  messages: entityStoreMessages,
});

// 儀表板「今日到期與逾期」任務快取

interface UpcomingTasksState {
  upcomingTasks: Task[];
  lastUpcomingTasksFetch: number | null;
  fetchUpcomingTasks: (force?: boolean) => Promise<void>;
}

const useUpcomingTasksStore = create<UpcomingTasksState>()(
  persist(
    (set, get) => ({
      upcomingTasks: [],
      lastUpcomingTasksFetch: null,

      // 背景載入、不顯示載入狀態（要麼顯示現有資料，要麼顯示「沒有緊急任務」）；有快取且不強制時不重抓
      fetchUpcomingTasks: async (force = false) => {
        if (!force && get().lastUpcomingTasksFetch) {
          return;
        }
        try {
          const response = await getUpcomingTasks();
          set({ upcomingTasks: response.data, lastUpcomingTasksFetch: Date.now() });
        } catch (error) {
          console.error('載入即將到期任務失敗:', error);
          set({ upcomingTasks: [] });
        }
      },
    }),
    {
      name: 'upcoming-tasks-store',
      partialize: (state) => ({
        upcomingTasks: state.upcomingTasks,
        lastUpcomingTasksFetch: state.lastUpcomingTasksFetch,
      }),
    }
  )
);

// 導出基礎 store 供其他模組使用（如果需要級聯刪除）
export { useTaskStoreBase, useUpcomingTasksStore };

export const useTaskStore = () => {
  const store = useTaskStoreBase();
  const upcomingStore = useUpcomingTasksStore();

  const confirmDelete = async () => {
    await store.confirmDelete();
    notifyDataChanged('tasks'); // 含「即將到期」任務重新載入
  };

  return {
    tasks: store.items,
    pagination: store.pagination,
    error: store.error,
    isPageLoading: store.isPageLoading,
    showDeleteConfirmModal: store.showDeleteConfirmModal,
    isDeleting: store.isDeleting,
    setCurrentPage: store.setCurrentPage,
    handleDelete: store.handleDelete,
    confirmDelete,
    closeDeleteModal: store.closeDeleteModal,
    fetchTasks: store.fetchItems,
    updateTask: store.updateItem,
    addTask: store.addItem,
    replaceTask: (tempId: string, realTask: Task) => store.updateItem(tempId, realTask),
    removeTask: (id: string) => removeItemsWhere<Task>(useTaskStoreBase, task => task.id === id),
    upcomingTasks: upcomingStore.upcomingTasks,
    fetchUpcomingTasks: upcomingStore.fetchUpcomingTasks,
  };
};
